import { faPhone } from "@fortawesome/free-solid-svg-icons";
import { faTruck, faTruckContainer, faTruckPickup } from "@fortawesome/pro-solid-svg-icons";
import { Document, Image, Link, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import React from "react";
import { MapObject } from "../../../models/MapObject";
import FontAwesomeIconPDF from "../../PDF/FontAwesomeIconPDF";
import PDFMappableAvatar from "../../PDF/PDFMappableAvatar";
import PDFTeamAvatar from "../../PDF/PDFTeamAvatar";
import PdfDocket from "../../PDF/Shared/PdfDocket";
import { PDF_FONT, PDF_INK, PDF_MUTED, PDF_ORANGE, PDF_RULE } from "../../PDF/Shared/pdfTheme";
import {
    DAMN_PDF_MAP_ASPECTS,
    DAMN_PDF_MAP_LABELS,
    DamnPdfMapKind,
    damnPdfMapLayout,
    damnPdfShownMaps,
} from "./damnPdfMaps";
import { DamnPdfInclude, DEFAULT_DAMN_PDF_INCLUDE } from "./damnPdfOptions";

export interface DamnPdfMappable {
    name: string;
    mapObject: MapObject | null;
}

export interface DamnPdfRoute extends DamnPdfMappable {
    steps: string[];
}

export interface DamnPdfLocation extends DamnPdfMappable {
    details: string | null;
}

export interface DamnPdfTeam {
    name: string;
    // An embedded data URL, or null when the team has no logo or it couldn't be fetched.
    logo: string | null;
}

export type DamnPdfVehicleSize = "light" | "medium" | "heavy";

export interface DamnPdfContact {
    name: string;
    phone: string | null;
}

export interface DamnPdfData {
    deliveryName: string;
    projectName: string;
    dateLabel: string;
    windowLabel: string;
    status: string;
    vehicle: { label: string; size: DamnPdfVehicleSize | null } | null;
    // The jobsite's required PPE, as set in the project's portal settings.
    ppe: string[];
    // The booking's "Special Instructions" (the calendar event's description), when there are any.
    instructions: string | null;
    haulRoute: DamnPdfRoute | null;
    // The approved routes to choose from when none is assigned; empty once one is.
    alternateHaulRoutes: DamnPdfMappable[];
    gate: DamnPdfLocation | null;
    gateToDropoffRoutes: DamnPdfRoute[];
    dropoff: DamnPdfLocation | null;
    equipment: DamnPdfMappable[];
    generalContractor: DamnPdfTeam | null;
    receivingTeam: DamnPdfTeam | null;
    // Null when the viewer isn't signed in: the DAMN page hides lead names and numbers then, and so does the PDF.
    contacts: DamnPdfContact[] | null;
    liveUrl: string;
    // A PNG data URL of a QR code to liveUrl, or null when it couldn't be drawn.
    qrCode: string | null;
    logoUrl: string;
    // A JPEG data URL per map once drawn (null if it couldn't be), and whether there's anything to show on it.
    maps: Record<DamnPdfMapKind, { available: boolean; image: string | null }>;
    // Whether the project has a GeoTIFF the maps can draw.
    geotiff: boolean;
    generatedLabel: string;
}

const ORANGE = PDF_ORANGE;
const INK = PDF_INK;
const MUTED = PDF_MUTED;
const RULE = PDF_RULE;
const SAFETY_RED = "#c81e1e";
// Letter is 612pt wide; 36pt margins leave 540pt for content.
const CONTENT_WIDTH = 540;
const MAP_GAP = 10;
// Shared by the delivery window box and the QR code beside it, so the two line up as one row. Tall enough for the box's
// label, date and window.
const TIME_ROW_HEIGHT = 66;
// The header's three lines (eyebrow, delivery name, jobsite), each kept to one line, and the GC logo beside them sized
// to their combined height.
const HEADER_LINE_HEIGHT = 1.36;
const HEADER_LINE_GAP = 2;
const EYEBROW_SIZE = 8;
const TITLE_SIZE = 18;
const SUBTITLE_SIZE = 12.5;
const GC_LOGO_SIZE = (EYEBROW_SIZE + TITLE_SIZE + SUBTITLE_SIZE) * HEADER_LINE_HEIGHT + HEADER_LINE_GAP * 2;
const HALF_WIDTH = (CONTENT_WIDTH - MAP_GAP) / 2;

// Tried in order until the manifest fits on one page: shorter maps first, then tighter text. `mapAspect` is for one
// full-width map and `pairAspect` for each of two side by side. The maps are captured with room to spare above and below
// (see damnPdfMaps), so the shorter frames only trim empty edges.
export const DAMN_PDF_DENSITIES = [
    {
        mapAspect: DAMN_PDF_MAP_ASPECTS.single.tallest,
        pairAspect: DAMN_PDF_MAP_ASPECTS.pair.tallest,
        value: 10.5,
        text: 9.5,
        note: 8.5,
        gap: 6,
    },
    { mapAspect: 2.3, pairAspect: 1.5, value: 10, text: 9, note: 8, gap: 5 },
    {
        mapAspect: DAMN_PDF_MAP_ASPECTS.single.shortest,
        pairAspect: DAMN_PDF_MAP_ASPECTS.pair.shortest,
        value: 9.5,
        text: 8,
        note: 7.5,
        gap: 3.5,
    },
] as const;

export type DamnPdfDensity = 0 | 1 | 2;

const createStyles = (density: DamnPdfDensity) => {
    const d = DAMN_PDF_DENSITIES[density];
    return StyleSheet.create({
        page: {
            paddingTop: 32,
            paddingBottom: 48,
            paddingHorizontal: 36,
            fontFamily: PDF_FONT,
            fontSize: d.text,
            color: INK,
        },
        header: { flexDirection: "row", justifyContent: "space-between", alignItems: "stretch", gap: 14 },
        headerText: { flex: 1, justifyContent: "center" },
        eyebrow: {
            fontSize: EYEBROW_SIZE,
            lineHeight: HEADER_LINE_HEIGHT,
            fontWeight: 900,
            color: ORANGE,
            letterSpacing: 1.2,
            textTransform: "uppercase",
        },
        // Cut short with an ellipsis rather than wrapping, so the header keeps the logo's height.
        title: {
            fontSize: TITLE_SIZE,
            lineHeight: HEADER_LINE_HEIGHT,
            fontWeight: 900,
            marginTop: HEADER_LINE_GAP,
            maxLines: 1,
            textOverflow: "ellipsis",
        },
        subtitle: {
            fontSize: SUBTITLE_SIZE,
            lineHeight: HEADER_LINE_HEIGHT,
            fontWeight: 600,
            marginTop: HEADER_LINE_GAP,
            maxLines: 1,
            textOverflow: "ellipsis",
        },
        // Like the logistics sheet's header logo, at the height of the three lines beside it.
        gcLogo: { width: GC_LOGO_SIZE, height: GC_LOGO_SIZE, borderRadius: 6, objectFit: "contain", alignSelf: "center" },
        timeBox: { width: 190, height: TIME_ROW_HEIGHT, alignSelf: "center" },
        qrArea: { alignItems: "center", justifyContent: "center", gap: 2, textDecoration: "none" },
        qrCode: { width: TIME_ROW_HEIGHT, height: TIME_ROW_HEIGHT },
        timeBody: { flex: 1, justifyContent: "center" },
        timeDate: { fontSize: 9.5, fontWeight: 900 },
        // The part drivers need most, so the largest, while "12:30 PM - 1:00 PM CDT" still fits on one line.
        timeWindow: { fontSize: 12.5, fontWeight: 900, marginTop: 1 },
        status: {
            fontSize: 6.5,
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: INK,
            backgroundColor: "#ffffff",
            borderWidth: 1,
            borderColor: ORANGE,
            borderRadius: 2,
            paddingVertical: 1.5,
            paddingHorizontal: 5,
        },
        maps: { marginTop: 12, flexDirection: "row", gap: MAP_GAP },
        map: { width: CONTENT_WIDTH, height: CONTENT_WIDTH / d.mapAspect, borderRadius: 6, objectFit: "cover" },
        mapHalf: { width: HALF_WIDTH, height: HALF_WIDTH / d.pairAspect, borderRadius: 6, objectFit: "cover" },
        mapHeading: { flexDirection: "row", alignItems: "baseline", gap: 5, marginBottom: 3 },
        mapTitle: { fontSize: 10.5, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.6 },
        mapDetail: { fontSize: 8, fontWeight: 600, color: MUTED },
        mapMissing: {
            height: 60,
            paddingHorizontal: 12,
            textAlign: "center",
            borderRadius: 6,
            borderWidth: 1,
            borderColor: RULE,
            alignItems: "center",
            justifyContent: "center",
            color: MUTED,
        },
        instructions: {
            marginTop: 10,
            paddingVertical: 6,
            paddingHorizontal: 10,
            backgroundColor: "#fff7e6",
            borderLeftWidth: 3,
            borderLeftColor: ORANGE,
            borderRadius: 4,
        },
        instructionsText: { fontSize: d.text, marginTop: 2, lineHeight: 1.35 },
        ppe: {
            marginTop: 10,
            paddingVertical: 6,
            paddingHorizontal: 10,
            backgroundColor: "#fdecea",
            borderLeftWidth: 3,
            borderLeftColor: SAFETY_RED,
            borderRadius: 4,
        },
        ppeLabel: { fontSize: 7, fontWeight: 900, color: SAFETY_RED, textTransform: "uppercase", letterSpacing: 0.8 },
        // Along wrapping lines rather than one per line, to keep the callout short.
        ppeList: { flexDirection: "row", flexWrap: "wrap", columnGap: 12, rowGap: 2, marginTop: 2 },
        ppeItem: { flexDirection: "row", gap: 4, fontSize: d.text, fontWeight: 600 },
        ppeMark: { fontWeight: 900, color: SAFETY_RED },
        columns: { flexDirection: "row", gap: 18, marginTop: 10 },
        column: { flex: 1 },
        sectionTitle: {
            fontSize: 7.5,
            fontWeight: 900,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: MUTED,
            borderBottomWidth: 1,
            borderBottomColor: RULE,
            paddingBottom: 2,
            marginBottom: d.gap,
        },
        item: { marginBottom: d.gap },
        itemLabel: { fontSize: 7, fontWeight: 900, color: ORANGE, textTransform: "uppercase", letterSpacing: 0.8 },
        itemValue: { fontSize: d.value, fontWeight: 600, marginTop: 1 },
        itemValueRow: { flexDirection: "row", alignItems: "center", gap: 5, marginTop: 1 },
        itemValueInRow: { fontSize: d.value, fontWeight: 600, flexShrink: 1 },
        itemNote: { fontSize: d.note, color: MUTED, marginTop: 1 },
        listRow: { flexDirection: "row", alignItems: "center", gap: 5, marginTop: 1 },
        inlineList: { flexDirection: "row", flexWrap: "wrap", columnGap: 10, rowGap: 2, marginTop: 1 },
        inlineItem: { flexDirection: "row", alignItems: "center", gap: 4 },
        inlineText: { fontSize: d.note, fontWeight: 600 },
        teamLogo: { width: 20, height: 20, borderRadius: 4, objectFit: "contain" },
        step: { flexDirection: "row", marginTop: 2 },
        stepNumber: { width: 13, fontWeight: 900, color: ORANGE },
        stepText: { flex: 1 },
        contact: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: 1 },
        phone: { flexDirection: "row", alignItems: "center", gap: 3 },
        footer: {
            position: "absolute",
            left: 36,
            right: 36,
            bottom: 20,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 7,
            color: MUTED,
            borderTopWidth: 1,
            borderTopColor: RULE,
            paddingTop: 5,
        },
        footerLeft: { flexDirection: "row", alignItems: "center", gap: 8 },
        footerLogo: { width: 44 },
        footerLink: { color: ORANGE, textDecoration: "none" },
        footerText: { flexDirection: "row", alignItems: "center" },
        footerQr: { marginLeft: 2 },
    });
};

type Styles = ReturnType<typeof createStyles>;
const StylesContext = React.createContext<Styles>(createStyles(0));
const useStyles = () => React.useContext(StylesContext);

const VEHICLE_ICONS = { light: faTruckPickup, medium: faTruck, heavy: faTruckContainer };

// The value line, with an avatar or logo in front of it when there is one.
const Value: React.FC<{ value: string; avatar?: React.ReactNode }> = ({ value, avatar }) => {
    const styles = useStyles();
    return avatar ? (
        <View style={styles.itemValueRow}>
            {avatar}
            <Text style={styles.itemValueInRow}>{value}</Text>
        </View>
    ) : (
        <Text style={styles.itemValue}>{value}</Text>
    );
};

const Item: React.FC<{ label: string; value: string; note?: string | null; avatar?: React.ReactNode }> = ({
    label,
    value,
    note,
    avatar,
}) => {
    const styles = useStyles();
    return (
        <View style={styles.item} wrap={false}>
            <Text style={styles.itemLabel}>{label}</Text>
            <Value value={value} avatar={avatar} />
            {note ? <Text style={styles.itemNote}>{note}</Text> : null}
        </View>
    );
};

const MappableList: React.FC<{ label: string; items: DamnPdfMappable[]; emptyText: string }> = ({
    label,
    items,
    emptyText,
}) => {
    const styles = useStyles();
    return (
        <View style={styles.item} wrap={false}>
            <Text style={styles.itemLabel}>{label}</Text>
            {items.length > 0 ? (
                items.map((item, index) => (
                    <View key={index} style={styles.listRow}>
                        <PDFMappableAvatar mapObject={item.mapObject} />
                        <Text style={styles.itemValueInRow}>{item.name}</Text>
                    </View>
                ))
            ) : (
                <Text style={styles.itemValue}>{emptyText}</Text>
            )}
        </View>
    );
};

// Secondary items (like routes the driver isn't assigned) run along one wrapping line to save height.
const InlineMappableList: React.FC<{ label: string; items: DamnPdfMappable[] }> = ({ label, items }) => {
    const styles = useStyles();
    return (
        <View style={styles.item} wrap={false}>
            <Text style={styles.itemLabel}>{label}</Text>
            <View style={styles.inlineList}>
                {items.map((item, index) => (
                    <View key={index} style={styles.inlineItem}>
                        <PDFMappableAvatar mapObject={item.mapObject} />
                        <Text style={styles.inlineText}>{item.name}</Text>
                    </View>
                ))}
            </View>
        </View>
    );
};

const TeamItem: React.FC<{ label: string; team: DamnPdfTeam | null }> = ({ label, team }) => {
    const styles = useStyles();
    return (
        <Item
            label={label}
            value={team?.name || "Not specified"}
            avatar={team?.logo ? <PDFTeamAvatar logo={team.logo} style={styles.teamLogo} /> : undefined}
        />
    );
};

const Steps: React.FC<{ steps: string[] }> = ({ steps }) => {
    const styles = useStyles();
    return (
        <>
            {steps.map((step, index) => (
                <View key={index} style={styles.step} wrap={false}>
                    <Text style={styles.stepNumber}>{index + 1}.</Text>
                    <Text style={styles.stepText}>{step}</Text>
                </View>
            ))}
        </>
    );
};

// Unlike Item, a route may run onto the next page between steps; its name stays with at least its first step.
const RouteItem: React.FC<{ label: string; route: DamnPdfRoute }> = ({ label, route }) => {
    const styles = useStyles();
    return (
        <View style={styles.item}>
            <View wrap={false} minPresenceAhead={route.steps.length > 0 ? 20 : 0}>
                <Text style={styles.itemLabel}>{label}</Text>
                <Value value={route.name} avatar={<PDFMappableAvatar mapObject={route.mapObject} />} />
            </View>
            <Steps steps={route.steps} />
        </View>
    );
};

interface DamnPdfDocumentProps {
    data: DamnPdfData;
    // Which sections the person exporting chose to include.
    include?: DamnPdfInclude;
    density?: DamnPdfDensity;
    // Called during layout with the page count, so the export can retry denser when it runs past one page.
    onPageCount?: (totalPages: number) => void;
}

const DamnPdfDocument: React.FC<DamnPdfDocumentProps> = ({
    data,
    include = DEFAULT_DAMN_PDF_INCLUDE,
    density = 0,
    onPageCount,
}) => {
    const styles = React.useMemo(() => createStyles(density), [density]);

    const withSteps = (route: DamnPdfRoute): DamnPdfRoute => (include.routeSteps ? route : { ...route, steps: [] });
    const showHaulRoute = include.haulRoute && !!data.haulRoute;
    const showOtherRoutes = include.otherRoutes && data.alternateHaulRoutes.length > 0;
    const showContacts = include.contacts && data.contacts !== null;
    const showPpe = include.ppe && data.ppe.length > 0;
    const showQr = include.qrCode && !!data.qrCode;
    const gcLogo = data.generalContractor?.logo;
    const shownMaps = damnPdfShownMaps(
        { approach: data.maps.approach.available, site: data.maps.site.available },
        include,
        !!data.haulRoute
    );
    const paired = damnPdfMapLayout(shownMaps) === "pair";
    const showRouteColumn =
        showHaulRoute ||
        showOtherRoutes ||
        (include.gate && !!data.gate) ||
        (include.gateToDropoffRoutes && data.gateToDropoffRoutes.length > 0) ||
        include.dropoff;
    const showDetailsColumn =
        include.equipment ||
        (include.vehicle && !!data.vehicle) ||
        include.generalContractor ||
        include.receivingTeam ||
        showContacts;

    return (
        <StylesContext.Provider value={styles}>
            <Document title={`${data.deliveryName} - Delivery Instructions`} author="PLOT">
                <Page size="LETTER" style={styles.page}>
                    <View style={styles.header}>
                        <PDFTeamAvatar logo={gcLogo} style={styles.gcLogo} />
                        <View style={styles.headerText}>
                            <Text style={styles.eyebrow}>Delivery Instructions</Text>
                            <Text style={styles.title}>{data.deliveryName}</Text>
                            <Text style={styles.subtitle}>{data.projectName}</Text>
                        </View>
                        <PdfDocket
                            label="Delivery window"
                            aside={data.status ? <Text style={styles.status}>{data.status}</Text> : null}
                            style={styles.timeBox}
                            bodyStyle={styles.timeBody}
                        >
                            <Text style={styles.timeDate}>{data.dateLabel}</Text>
                            <Text style={styles.timeWindow}>{data.windowLabel}</Text>
                        </PdfDocket>
                        {showQr && data.qrCode ? (
                            <Link src={data.liveUrl} style={styles.qrArea}>
                                <Image style={styles.qrCode} src={data.qrCode} />
                            </Link>
                        ) : null}
                    </View>

                    {shownMaps.length > 0 ? (
                        <View style={styles.maps}>
                            {shownMaps.map((kind) => (
                                <View key={kind}>
                                    <View style={styles.mapHeading}>
                                        <Text style={styles.mapTitle}>{DAMN_PDF_MAP_LABELS[kind].title}</Text>
                                        <Text style={styles.mapDetail}>{DAMN_PDF_MAP_LABELS[kind].detail}</Text>
                                    </View>
                                    {data.maps[kind].image ? (
                                        <Image
                                            style={paired ? styles.mapHalf : styles.map}
                                            src={data.maps[kind].image}
                                        />
                                    ) : (
                                        <View
                                            style={[styles.mapMissing, { width: paired ? HALF_WIDTH : CONTENT_WIDTH }]}
                                        >
                                            <Text>Map unavailable. Use the live directions link below.</Text>
                                        </View>
                                    )}
                                </View>
                            ))}
                        </View>
                    ) : null}

                    {include.instructions && data.instructions ? (
                        <View style={styles.instructions}>
                            <Text style={styles.itemLabel}>Special instructions</Text>
                            <Text style={styles.instructionsText}>{data.instructions}</Text>
                        </View>
                    ) : null}

                    {showRouteColumn || showDetailsColumn ? (
                        <View style={styles.columns}>
                            <View style={styles.column}>
                                {showRouteColumn ? <Text style={styles.sectionTitle}>Route to the dropoff</Text> : null}
                                {showHaulRoute && data.haulRoute ? (
                                    <RouteItem label="Haul route" route={withSteps(data.haulRoute)} />
                                ) : null}
                                {showOtherRoutes ? (
                                    <InlineMappableList label="Approved haul routes" items={data.alternateHaulRoutes} />
                                ) : null}
                                {include.gate && data.gate ? (
                                    <Item
                                        label="Enter at gate"
                                        value={data.gate.name}
                                        note={data.gate.details}
                                        avatar={<PDFMappableAvatar mapObject={data.gate.mapObject} />}
                                    />
                                ) : null}
                                {include.gateToDropoffRoutes
                                    ? data.gateToDropoffRoutes.map((route, index) => (
                                          <RouteItem key={index} label="Gate to dropoff" route={withSteps(route)} />
                                      ))
                                    : null}
                                {include.dropoff ? (
                                    <Item
                                        label="Dropoff"
                                        value={data.dropoff?.name || "Not specified"}
                                        note={data.dropoff?.details}
                                        avatar={
                                            data.dropoff ? (
                                                <PDFMappableAvatar mapObject={data.dropoff.mapObject} />
                                            ) : undefined
                                        }
                                    />
                                ) : null}
                            </View>

                            <View style={styles.column}>
                                {showDetailsColumn ? <Text style={styles.sectionTitle}>Delivery details</Text> : null}
                                {include.equipment ? (
                                    <MappableList
                                        label="Unloading equipment"
                                        items={data.equipment}
                                        emptyText="None specified"
                                    />
                                ) : null}
                                {include.vehicle && data.vehicle ? (
                                    <Item
                                        label="Vehicle"
                                        value={data.vehicle.label}
                                        avatar={
                                            data.vehicle.size ? (
                                                <FontAwesomeIconPDF
                                                    faIcon={VEHICLE_ICONS[data.vehicle.size]}
                                                    style={{ color: ORANGE, width: "14pt" }}
                                                />
                                            ) : undefined
                                        }
                                    />
                                ) : null}
                                {include.generalContractor ? (
                                    <TeamItem label="General contractor" team={data.generalContractor} />
                                ) : null}
                                {include.receivingTeam ? (
                                    <TeamItem label="Receiving team" team={data.receivingTeam} />
                                ) : null}
                                {showContacts && data.contacts ? (
                                    <View style={styles.item} wrap={false}>
                                        <Text style={styles.itemLabel}>Receiving team contacts</Text>
                                        {data.contacts.length > 0 ? (
                                            data.contacts.map((contact, index) => (
                                                <View key={index} style={styles.contact}>
                                                    <Text style={{ fontWeight: 600 }}>{contact.name}</Text>
                                                    {contact.phone ? (
                                                        <View style={styles.phone}>
                                                            <FontAwesomeIconPDF
                                                                faIcon={faPhone}
                                                                style={{ color: ORANGE, width: "8pt" }}
                                                            />
                                                            <Text>{contact.phone}</Text>
                                                        </View>
                                                    ) : null}
                                                </View>
                                            ))
                                        ) : (
                                            <Text style={styles.itemValue}>No leads specified</Text>
                                        )}
                                    </View>
                                ) : null}
                            </View>
                        </View>
                    ) : null}

                    {/* Last on the page and in safety red, so it's the thing drivers are left with: they're turned away
                        at the gate without it. */}
                    {showPpe ? (
                        <View style={styles.ppe} wrap={false}>
                            <Text style={styles.ppeLabel}>Required PPE</Text>
                            <View style={styles.ppeList}>
                                {data.ppe.map((item, index) => (
                                    <View key={index} style={styles.ppeItem}>
                                        <Text style={styles.ppeMark}>•</Text>
                                        <Text>{item}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    ) : null}

                    <View style={styles.footer} fixed>
                        <View style={styles.footerLeft}>
                            <Image style={styles.footerLogo} src={data.logoUrl} />

                            <View style={styles.footerText}>
                                <Text>
                                    Live directions and updates at{" "}
                                    <Link src={data.liveUrl} style={styles.footerLink}>
                                        {data.liveUrl}
                                    </Link>
                                </Text>
                                {/* The footer repeats on every page, but the QR code is only in the first page's header. */}
                                {showQr ? (
                                    <Text
                                        style={styles.footerQr}
                                        render={({ pageNumber }) => (pageNumber === 1 ? "or QR above" : "")}
                                    />
                                ) : null}
                            </View>
                        </View>
                        <Text
                            render={({ pageNumber, totalPages }) => {
                                onPageCount?.(totalPages);
                                return `${data.generatedLabel} · Page ${pageNumber} of ${totalPages}`;
                            }}
                        />
                    </View>
                </Page>
            </Document>
        </StylesContext.Provider>
    );
};

export default DamnPdfDocument;
